// userDetailsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";
import { getUserData } from "../../../utils/userDataSource";

const BASE_URL = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

// Simple request deduplication cache
const requestCache = new Map();
const CACHE_DURATION = 10000; // 10 seconds - much more aggressive

// Debouncing for API calls to prevent spam
const debounceMap = new Map();
const DEBOUNCE_TIME = 5000; // 5 seconds

const shouldDebounce = (key) => {
  const now = Date.now();
  const lastCall = debounceMap.get(key);
  
  if (lastCall && (now - lastCall) < DEBOUNCE_TIME) {
    return true; // Should debounce (skip this call)
  }
  
  debounceMap.set(key, now);
  return false; // Don't debounce (allow this call)
};

const createCachedThunk = (key, asyncThunk) => {
  return (...args) => (dispatch, getState) => {
    const force = args[0]?.force === true;
    const now = Date.now();
    const cached = requestCache.get(key);

    // If request was made recently, skip it (unless the caller explicitly forces a refresh)
    if (!force && cached && (now - cached.timestamp) < CACHE_DURATION) {
      console.log(`🚫 Skipping duplicate ${key} request (cached ${now - cached.timestamp}ms ago)`);
      return Promise.resolve(cached.result);
    }

    // Make the request and cache it
    console.log(`📡 Making ${key} request${force ? " (forced)" : ""}`);
    const promise = dispatch(asyncThunk(...args));
    requestCache.set(key, { timestamp: now, result: promise });

    // Don't let a genuine failure (e.g. "No token found" from a pre-login call)
    // poison the cache/debounce window for the full TTL - clear both immediately
    // so the next call gets a real retry instead of replaying the same stale
    // rejection. Skip this for our own "Debounced" skips, which aren't failures.
    promise.then((action) => {
      if (action?.meta?.requestStatus === "rejected" && action?.payload !== "Debounced") {
        if (requestCache.get(key)?.result === promise) {
          requestCache.delete(key);
        }
        debounceMap.delete(key);
      }
    });

    return promise;
  };
};

// Clears both module-level caches — call on logout so the next account's
// first fetch is never silently skipped/served from the previous account's data.
export const __resetUserDetailsCaches = () => {
  requestCache.clear();
  debounceMap.clear();
};

// Async thunk to fetch user details with authorization header
// (raw/uncached — external callers should use the cached export below, not this directly)
const fetchUserDetailsRaw = createAsyncThunk(
  "userDetails/fetchUserDetails",
  async (arg, { rejectWithValue }) => {
    // Debounce check
    if (shouldDebounce('fetchUserDetails')) {
      return rejectWithValue("Debounced");
    }

    try {
      // Shared with authSlice.js's buildUserData - same underlying cache,
      // so the browser makes one real request per window regardless of
      // which slice asked first.
      const data = await getUserData({ force: arg?.force === true });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Async thunk to fetch user permissions with authorization header
// (raw/uncached — external callers should use the cached export below, not this directly)
const fetchUserPermissionsRaw = createAsyncThunk(
  "fetchUserPermissions",
  async (_, { rejectWithValue }) => {
    if (shouldDebounce('fetchUserPermissions')) {
      return rejectWithValue("Debounced");
    }

    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.get(`${BASE_URL}/api/auth/permissions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Define the initial state for the userDetails slice
const initialState = {
  userDetails: null, // To store fetched user details
  userPermissions: null, // To store fetched user permissions
  loading: false,
  error: null,
};

// Create the userDetails slice
const userDetailsSlice = createSlice({
  name: "userDetails",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserDetailsRaw.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserDetailsRaw.fulfilled, (state, action) => {
        state.loading = false;
        state.userDetails = action.payload; // Store the user details in the state
      })
      .addCase(fetchUserDetailsRaw.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchUserPermissionsRaw.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserPermissionsRaw.fulfilled, (state, action) => {
        state.loading = false;
        state.userPermissions = action.payload; // Store the user permissions in the state
      })
      .addCase(fetchUserPermissionsRaw.rejected, (state, action) => {
        state.loading = false;
        if (action.payload !== "Debounced") {
          state.error = action.payload;
        }
      });
  },
});

// The names every existing caller imports — now cached by default (10s TTL, pass
// {force:true} to bypass, e.g. right after a save that must show fresh data).
export const fetchUserDetails = createCachedThunk('fetchUserDetails', fetchUserDetailsRaw);
export const fetchUserPermissions = createCachedThunk('fetchUserPermissions', fetchUserPermissionsRaw);

// Kept as aliases so call sites that already import these explicit names keep working unchanged.
export const fetchUserDetailsCached = fetchUserDetails;
export const fetchUserPermissionsCached = fetchUserPermissions;

// Export the reducer to be used in store.js
export default userDetailsSlice.reducer;
